import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Boxes, CheckCircle2, AlertTriangle, XCircle, Inbox, Sparkles, Trash2 } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Btn, ConfirmDialog } from "./kit";
import { toast } from "sonner";

const COLORS = ["#1B3A2B", "#C88A2C", "#2E211B", "#5a8f6f", "#e0b872"];

const StatCard = ({ icon: Icon, label, value, tone = "forest", testId }) => (
  <div data-testid={testId} className="rounded-xl border border-offwhite/10 bg-[#101c14] p-5">
    <div className="flex items-center justify-between">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone === "amber" ? "bg-amber-brand/20 text-amber-soft" : tone === "rose" ? "bg-rose-500/15 text-rose-300" : "bg-forest/40 text-emerald-300"}`}><Icon className="h-5 w-5" /></div>
    </div>
    <div className="mt-4 font-display text-3xl font-extrabold text-offwhite">{value}</div>
    <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-offwhite/50">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = async () => { try { const r = await api.get("/admin/dashboard"); setData(r.data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const seed = async () => { try { await api.post("/admin/seed-demo"); toast.success("Demo data loaded"); load(); } catch (e) { toast.error("Failed"); } };
  const purge = async () => { try { await api.delete("/admin/demo-data"); toast.success("Demo data removed"); setConfirm(false); load(); } catch (e) { toast.error("Failed"); } };

  if (!data) return <div className="font-mono text-sm text-offwhite/50">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-offwhite">Dashboard</h1>
          <p className="mt-1 font-body text-sm text-offwhite/55">Overview of inventory and inquiries.</p>
        </div>
        <div className="flex gap-2">
          <Btn variant="outline" onClick={seed} data-testid="seed-demo-btn"><Sparkles className="mr-1 inline h-3.5 w-3.5" /> Load Demo Data</Btn>
          <Btn variant="ghost" onClick={() => setConfirm(true)} data-testid="purge-demo-btn"><Trash2 className="mr-1 inline h-3.5 w-3.5" /> Clear Demo</Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard icon={Boxes} label="Total Products" value={data.total_products} testId="stat-total-products" />
        <StatCard icon={CheckCircle2} label="Available" value={data.available_stock} testId="stat-available" />
        <StatCard icon={AlertTriangle} label="Low Stock" value={data.low_stock} tone="amber" testId="stat-low-stock" />
        <StatCard icon={XCircle} label="Out of Stock" value={data.out_of_stock} tone="rose" testId="stat-out-stock" />
        <StatCard icon={Inbox} label="Pending RFQs" value={data.pending_rfqs} tone="amber" testId="stat-pending-rfqs" />
        <StatCard icon={Inbox} label="Total Inquiries" value={data.recent_inquiries} testId="stat-total-rfqs" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-offwhite/10 bg-[#101c14] p-5 lg:col-span-2">
          <h3 className="mb-4 font-display text-sm font-bold text-offwhite">Stock by Product</h3>
          {data.stock_by_product.length === 0 ? <p className="py-10 text-center font-body text-sm text-offwhite/40">No products yet.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.stock_by_product}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                <XAxis dataKey="name" tick={{ fill: "#ffffff70", fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fill: "#ffffff70", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0b130e", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="stock" fill="#C88A2C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-xl border border-offwhite/10 bg-[#101c14] p-5">
          <h3 className="mb-4 font-display text-sm font-bold text-offwhite">Stock Status</h3>
          {data.status_distribution.length === 0 ? <p className="py-10 text-center font-body text-sm text-offwhite/40">No data.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.status_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={{ fill: "#fff", fontSize: 10 }}>
                  {data.status_distribution.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0b130e", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-offwhite/10 bg-[#101c14] p-5">
          <h3 className="mb-4 font-display text-sm font-bold text-offwhite">Recent Stock Updates</h3>
          <div className="space-y-2">
            {data.recent_products.length === 0 ? <p className="font-body text-sm text-offwhite/40">No products.</p> : data.recent_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-offwhite/5 px-4 py-3">
                <span className="font-body text-sm text-offwhite/85">{p.product_name}</span>
                <span className="font-mono text-xs text-offwhite/55">{p.available_stock} {p.stock_unit}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-offwhite/10 bg-[#101c14] p-5">
          <h3 className="mb-4 font-display text-sm font-bold text-offwhite">Recent Inquiries</h3>
          <div className="space-y-2">
            {data.recent_rfqs.length === 0 ? <p className="font-body text-sm text-offwhite/40">No inquiries yet.</p> : data.recent_rfqs.map((r, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-offwhite/5 px-4 py-3">
                <div><div className="font-mono text-xs text-amber-soft">{r.rfq_number}</div><div className="font-body text-sm text-offwhite/85">{r.company} · {r.product}</div></div>
                <span className="rounded-full bg-forest/40 px-2.5 py-1 font-mono text-[10px] uppercase text-emerald-300">{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} onConfirm={purge} message="Remove all demo (sample) products? This only deletes records marked as demo data." />
    </div>
  );
}
